import { Widget } from '@features/dashboard/types';
import { useApi } from '@hooks/useApi';
import { useAppAuthentication } from '@hooks/useAppAuthentication';
import { Dashboard } from '@protos/v2/dashboard';
import { logger, streamV2 } from '@services/context';
import { ChannelType, StreamEvent, Subscription } from '@services/StreamV2';
import { toastifyService } from '@services/ToastifyService';
import { useCallback, useEffect, useState } from 'react';

const generateNewDashboardName = (allDashboards: Dashboard[]) => {
  const keyword = 'dashboard';
  const data = allDashboards || [];
  const index = data.reduce((maxIndex, d) => {
    const bits = d.name.split('-');
    if (bits.length === 2 && bits[0] === keyword) {
      const index = +bits[1];
      if (index > maxIndex) return index;
    }
    return maxIndex;
  }, 0);
  return `${keyword}-${index + 1}`;
};

export const useStreamV2DashboardService = () => {
  const { apiClient } = useApi();
  const { isAuthenticated } = useAppAuthentication();
  const [allDashboards, setAllDashboards] = useState<Dashboard[]>([]);
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | undefined>(allDashboards[0]);
  const [deletingDashboard, setDeletingDashboard] = useState<number | undefined>(undefined);
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [isCreatingDefaultDashboard, setIsCreatingDefaultDashboard] = useState(false);
  const [isDoneInitialFetch, setIsDoneInitialFetch] = useState(false);

  useEffect(() => {
    streamV2.onConnect(() => {
      streamV2.subscribe(Subscription.dashboards());
    }, ChannelType.Dashboards);
  }, [streamV2]);

  const loadDashboard = useCallback(
    async (updatedDashboard: Dashboard) => {
      const transformedUpdatedDashboard = {
        ...updatedDashboard,
        websocket_uid: streamV2.uid || '',
        id: currentDashboard?.id || 0,
        name: currentDashboard?.name || '',
      };
      setAllDashboards(prev => prev.map(dashboard => (dashboard.id === transformedUpdatedDashboard.id ? transformedUpdatedDashboard : dashboard)));
      if (currentDashboard?.id === transformedUpdatedDashboard.id) {
        setCurrentDashboard(transformedUpdatedDashboard);
      }
      await streamV2.modifyDashboard(transformedUpdatedDashboard.id, transformedUpdatedDashboard);
      return transformedUpdatedDashboard;
    },
    [currentDashboard, streamV2]
  );

  const updateDashboard = useCallback(
    async (updatedDashboard: Dashboard) => {
      const transformedUpdatedDashboard = { ...updatedDashboard, websocket_uid: streamV2.uid || '' };

      setAllDashboards(prev => prev.map(dashboard => (dashboard.id === transformedUpdatedDashboard.id ? transformedUpdatedDashboard : dashboard)));
      if (currentDashboard?.id === transformedUpdatedDashboard.id) {
        setCurrentDashboard(transformedUpdatedDashboard);
      }

      await streamV2.modifyDashboard(transformedUpdatedDashboard.id, transformedUpdatedDashboard);
      return transformedUpdatedDashboard;
    },
    [currentDashboard, streamV2]
  );

  const setCurrentDashboardById = useCallback(
    (id: string) => {
      const dashboard = allDashboards.find(d => d.id.toString() === id);
      if (dashboard) {
        setCurrentDashboard(dashboard);
        if (isDashboardLoading) setIsDashboardLoading(false);
      }
    },
    [allDashboards]
  );

  const addDashboard = useCallback(
    async (overrideDashboardName?: string) => {
      const newDashboard = await apiClient?.createDashboard({
        name: overrideDashboardName || generateNewDashboardName(allDashboards),
        json: {},
        websocket_uid: streamV2.uid,
      });

      setAllDashboards(prev => [...prev, newDashboard as Dashboard]);
      setCurrentDashboardById(newDashboard?.id.toString() || '');

      return newDashboard;
    },
    [allDashboards, streamV2, setCurrentDashboardById, apiClient]
  );

  const deleteDashboard = useCallback(
    async (id: string) => {
      const transformedDashboards = allDashboards.filter(dashboard => dashboard.id.toString() !== id);

      setAllDashboards(transformedDashboards);
      if (currentDashboard?.id.toString() === id) {
        setCurrentDashboard(transformedDashboards[0]);
      }
      setDeletingDashboard(Number(id));

      await apiClient?.deleteDashboard(+id);

      // A temporary workaround to handle deleting dashboards until websockets get correct messageType
      setTimeout(() => {
        setDeletingDashboard(undefined);
      }, 5000);
    },
    [allDashboards, currentDashboard, apiClient]
  );

  const clearCurrentDashboard = useCallback(() => {
    setCurrentDashboard(undefined);
  }, []);

  const editWidgetPayloadById = useCallback(
    async (id: string, newPayload: Record<string, any>) => {
      try {
        if (!currentDashboard) return;

        const newJsonWidgets = currentDashboard.json?.widgets?.map((widget: Widget) => {
          if (widget.id === id) {
            widget.payload = { ...widget.payload, ...newPayload };
          }
          return widget;
        });
        const transformedDashboardJson = { ...currentDashboard.json, widgets: newJsonWidgets };

        const newDashboard = { ...currentDashboard, json: transformedDashboardJson };
        await updateDashboard(newDashboard);
      } catch (error: any) {
        toastifyService.showErrorMessage('Something went wrong, please refresh and try again');
        logger.error('Unexpected error', error);
      }
    },
    [currentDashboard, updateDashboard]
  );

  const deleteWidgetById = useCallback(
    async (id: string) => {
      if (!currentDashboard) return;

      const newJsonWidgets = (currentDashboard.json?.widgets || []).filter((widget: Widget) => widget.id !== id);
      const transformedDashboardJson = { ...currentDashboard.json, widgets: newJsonWidgets };

      const newDashboard = { ...currentDashboard, json: transformedDashboardJson };
      await updateDashboard(newDashboard);
    },
    [currentDashboard, updateDashboard]
  );

  const addNewWidgets = useCallback(
    async (widgets: Widget[]) => {
      if (!currentDashboard || !currentDashboard.json) return;
      const transformedDashboardJson = { ...currentDashboard.json, widgets: [...(currentDashboard.json.widgets || []), ...widgets] };
      const newDashboard = { ...currentDashboard, json: transformedDashboardJson };
      await updateDashboard(newDashboard);
      setCurrentDashboard(newDashboard);
    },
    [currentDashboard, currentDashboard?.json.widgets, updateDashboard]
  );

  const editDashboardLayouts = useCallback(
    async (layouts: any) => {
      if (!currentDashboard) return;

      const newJson = { ...currentDashboard.json, layouts: { ...currentDashboard.json?.layouts, ...layouts } };
      const newDashboard = { ...currentDashboard, json: newJson };
      await updateDashboard(newDashboard);
    },
    [currentDashboard, updateDashboard]
  );

  const createDefaultDashboard = useCallback(async () => {
    if (isCreatingDefaultDashboard) return;
    if (!isAuthenticated) {
      logger.warn('Not authenticated, cannot create default dashboard');
      return null;
    }
    try {
      setIsCreatingDefaultDashboard(true);
      const dashboard = await apiClient?.createDashboard({
        name: 'Default',
        json: {},
        websocket_uid: streamV2.uid || '',
      });
      setAllDashboards(prev => [...prev, dashboard as Dashboard]);
      setCurrentDashboard(dashboard);
      setIsDashboardLoading(false);
      return dashboard;
    } catch (error: any) {
      toastifyService.showErrorMessage('Error creating default dashboard');
      logger.warn('Error creating default dashboard', error);
      return null;
    } finally {
      setIsCreatingDefaultDashboard(false);
    }
  }, [isCreatingDefaultDashboard, isAuthenticated, streamV2.uid, apiClient]);

  const getDashboards = useCallback(async () => {
    if (!apiClient) return;
    if (!isAuthenticated) return;
    try {
      const dashboards = await apiClient.getDashboards();
      setAllDashboards(dashboards.data);
      setIsDoneInitialFetch(true);
    } catch (error: any) {
      toastifyService.showErrorMessage('Error fetching dashboards');
      logger.warn('Error fetching dashboards', error);
    }
  }, [apiClient, isAuthenticated]);

  useEffect(() => {
    if (!isDoneInitialFetch && isAuthenticated && apiClient) {
      getDashboards();
    }
  }, [allDashboards, getDashboards, isDoneInitialFetch, isAuthenticated, apiClient]);

  const subscribeToDashboards = useCallback(
    (onDefaultDashboardCreated?: (dashboard: Dashboard) => void) => {
      const handleDashboardEvent = async (event: StreamEvent) => {
        const eventData = event.asDashboards();
        if (!eventData) return;

        const isLocalMessageEvent = !eventData.isSnapshot && eventData.dashboards.length && eventData.dashboards[0].websocket_uid === streamV2.uid;
        if (isLocalMessageEvent) {
          if (isDashboardLoading && currentDashboard) setIsDashboardLoading(false);
          return;
        }

        eventData.dashboards.forEach(dashboard => {
          if (deletingDashboard && deletingDashboard === dashboard.id) return;

          const isAddDashboardEvent = dashboard.id && isDoneInitialFetch && !allDashboards.some(d => d.id === dashboard.id);

          if (isAddDashboardEvent) {
            setAllDashboards(prev => [...prev, dashboard]);

            if (currentDashboard?.id !== dashboard.id) {
              setCurrentDashboard(dashboard);
              if (isDashboardLoading) setIsDashboardLoading(false);
            }
            const isSharedDashboard = !eventData.isSnapshot && dashboard.name && !allDashboards.find(d => d.id === dashboard.id);
            if (isSharedDashboard) {
              toastifyService.showSuccess(`Dashboard "${dashboard.name}" has been added to your dashboards`);
            }
          }
        });

        if (isDashboardLoading && (currentDashboard || !window.location.pathname.includes('dashboard'))) setIsDashboardLoading(false);

        const isNoDashboards = eventData.dashboards.length === 0 && eventData.isSnapshot && allDashboards.length === 0;
        if (isNoDashboards) {
          const dashboard = await createDefaultDashboard();
          if (dashboard && onDefaultDashboardCreated) {
            onDefaultDashboardCreated(dashboard);
          }
        }
      };

      const unsubscribe = streamV2.onEvent(ChannelType.Dashboards, handleDashboardEvent);

      return { unsubscribe };
    },
    [
      allDashboards,
      streamV2,
      isDashboardLoading,
      currentDashboard,
      deletingDashboard,
      createDefaultDashboard,
      isCreatingDefaultDashboard,
      streamV2.uid,
    ]
  );

  return {
    allDashboards,
    currentDashboard,
    addDashboard,
    updateDashboard,
    deleteDashboard,
    setCurrentDashboardById,
    editWidgetPayloadById,
    deleteWidgetById,
    addNewWidgets,
    clearCurrentDashboard,
    isDashboardLoading,
    editDashboardLayouts,
    createDefaultDashboard,
    subscribeToDashboards,
    loadDashboard,
  };
};
